// Mortgage calculation utilities — pure functions, no browser dependencies
// Imported by property.js (frontend) and tested directly in vitest.

/**
 * Calculate monthly mortgage payment (principal + interest only).
 * @param {number} purchasePrice  - Total purchase price in dollars
 * @param {number} downPct        - Down payment percentage (0-100)
 * @param {number} annualRatePct  - Annual interest rate percentage (e.g. 7.0)
 * @param {number} years          - Loan term in years
 * @returns {number} Monthly P&I payment in dollars
 */
export function calcMonthlyMortgage(purchasePrice, downPct, annualRatePct, years) {
  const principal = purchasePrice * (1 - downPct / 100);
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/**
 * Derive a price estimate from property API data across all regions.
 * Returns null if no usable value is found.
 * @param {object} p - Property object from /api/property
 * @returns {number|null}
 */
export function estimatePriceFromProperty(p) {
  if (!p) return null;
  if (p.full_market_value && p.full_market_value > 0) return p.full_market_value;
  if (p.appraised_total && p.appraised_total > 0) return p.appraised_total;
  if (p.net_value && p.net_value > 0) return p.net_value;
  if (p.assessed_total && p.assessed_total > 0) return p.assessed_total * 4;
  if (p.assessed_land != null && p.assessed_improvement != null) {
    const total = (p.assessed_land || 0) + (p.assessed_improvement || 0);
    return total > 0 ? total : null;
  }
  return null;
}
